import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Sport } from '__generated__/globalTypes';
import { isType } from 'lib/gql';
import { lazy } from 'lib/retry';

import type { DeliverableReward_deliverableItemInterface } from './__generated__/index.graphql';
import baseballTickets from './assets/baseball_tickets.webp';
import footballTickets from './assets/football_tickets.webp';
import nbaTickets from './assets/nba_tickets.webp';

const Jersey = lazy(
  async () => import('@sorare/trois/src/atoms/Jersey'),
  'Jersey'
);

const Picture = lazy(
  async () => import('@sorare/trois/src/atoms/Picture'),
  'Picture'
);

const ticketsUrl = {
  [Sport.FOOTBALL]: footballTickets,
  [Sport.BASEBALL]: baseballTickets,
  [Sport.NBA]: nbaTickets,
};

type Props = {
  deliverableItem: DeliverableReward_deliverableItemInterface;
};

export const DeliverableReward = ({ deliverableItem }: Props) => {
  if (isType(deliverableItem, 'JerseyDeliverableItem')) {
    const { anyTeam, signed } = deliverableItem;
    return (
      <Jersey
        color={anyTeam?.styleGuideColor || ''}
        logoUrl={anyTeam?.pictureUrl || ''}
        rotation-x={Math.PI / 12}
        rotation-y={Math.PI / -10}
        signed={signed}
        scale={0.35}
      />
    );
  }
  if (isType(deliverableItem, 'TicketsDeliverableItem')) {
    const { sport } = deliverableItem;
    return <Picture url={ticketsUrl[sport]} />;
  }

  // eslint-disable-next-line no-console
  console.error('Unknown deliverable item type', deliverableItem);
  return null;
};

DeliverableReward.fragments = {
  deliverableItemInterface: gql`
    fragment DeliverableReward_deliverableItemInterface on DeliverableItemInterface {
      slug
      sport
      ... on JerseyDeliverableItem {
        slug
        anyTeam {
          slug
          pictureUrl
          styleGuideColor
        }
        signed
      }
      ... on TicketsDeliverableItem {
        slug
      }
    }
  ` as TypedDocumentNode<DeliverableReward_deliverableItemInterface>,
};
